import { useQuery } from '@tanstack/react-query'
import { Card, Flex, Skeleton, Typography } from 'antd'
import { LayoutGrid, ShieldCheck, ShieldHalf, Users } from 'lucide-react'
import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  applicationsApplicationsListOptions,
  organizationsAccessListOptions,
  organizationsOrganizationsAuthMethodsRetrieve,
  type Organization
} from 'src/api/main'
import { SoonTag } from 'src/components'
import { organizationAuthMethodsQueryKey } from 'src/features/organizations/components'
import { useFeatureTelemetry } from 'src/flags'
import { useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router'
import { WorkspacePermissionCode } from 'src/utils'

import { useStyles } from './styles'

const { Text } = Typography

type Props = {
  workspace: Organization
}

// A tile links to a section; without the section's own code the destination is
// the "not enough permissions" screen, so the tile is dropped instead. The auth
// tiles point at Authentication, which the sidebar gates on `organization.manage`
// even though the counts behind them read on `organization.view`.
const SECTION_CODES = [
  WorkspacePermissionCode.USERS_VIEW,
  WorkspacePermissionCode.APPLICATIONS_VIEW,
  WorkspacePermissionCode.ORGANIZATION_MANAGE
]

type KpiTile = {
  key: string
  icon: ReactNode
  label: string
  value: string | number | undefined
  loading: boolean
  href: string
  soon?: boolean
}

export const OverviewDetails = ({ workspace }: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  // P1 scope gating: "soon" stat tiles (no real backing yet) show only when the
  // telemetry flag is ON; the real API-backed tiles always render.
  const telemetryOn = useFeatureTelemetry()
  const [mayViewMembers, mayViewApps, mayManageWorkspace] =
    useHasWorkspacePermissions(SECTION_CODES)
  const id = String(workspace.id)
  const workspaceId = workspace.id

  const enabled = Number.isFinite(workspaceId)
  const staleTime = 30_000

  // Each count comes from the section's own endpoint, so a member without that
  // section's code must not ask for it — the tile is dropped below either way
  // and the request would only come back 403.

  const { data: membersPage, isLoading: membersLoading } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 1 }
    }),
    enabled: enabled && mayViewMembers,
    staleTime
  })

  const { data: appsPage, isLoading: appsLoading } = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId, page: 1, per_page: 1 }
    }),
    enabled: enabled && mayViewApps,
    staleTime
  })

  // Returns the full envelope so the cache entry under this key is
  // interchangeable with the one written by OrganizationAuthMethodsCard —
  // diverging shapes here would silently clobber its enforce_sso / SSO
  // toggle reads. Local count is derived from data.auth_methods below.
  const { data: authMethodsEnvelope, isLoading: authLoading } = useQuery({
    queryKey: organizationAuthMethodsQueryKey(workspaceId),
    queryFn: async () => {
      const { data } = await organizationsOrganizationsAuthMethodsRetrieve({
        path: { id: workspaceId }
      })

      return data
    },
    enabled: enabled && mayManageWorkspace,
    staleTime
  })

  const enabledAuthMethodsCount =
    authMethodsEnvelope?.auth_methods?.filter((m) => m.enabled).length ?? 0

  const tiles: KpiTile[] = [
    {
      key: 'members',
      icon: <Users size={14} />,
      label: t('workspaceOverview.details.stats.members'),
      value: membersPage?.count,
      loading: membersLoading,
      href: paths.workspaces.members(id)
    },
    {
      key: 'apps',
      icon: <LayoutGrid size={14} />,
      label: t('workspaceOverview.details.stats.apps'),
      value: appsPage?.count,
      loading: appsLoading,
      href: paths.workspaces.apps.root(id)
    },
    {
      key: 'auth',
      icon: <ShieldCheck size={14} />,
      label: t('workspaceOverview.details.stats.authMethods'),
      value: enabledAuthMethodsCount,
      loading: authLoading,
      href: paths.workspaces.security(id)
    },
    {
      key: 'mfa',
      icon: <ShieldHalf size={14} />,
      label: t('workspaceOverview.details.stats.mfaCoverage'),
      value: '—',
      loading: false,
      href: paths.workspaces.security(id),
      soon: true
    }
  ]

  // The Roles tile is dropped when its flag is OFF: the route redirects to Home,
  // so the tile would link nowhere (its count query is skipped above too).
  const allowedTileKeys: Record<string, boolean> = {
    members: mayViewMembers,
    apps: mayViewApps,
    auth: mayManageWorkspace,
    mfa: mayManageWorkspace
  }

  const visibleTiles = tiles.filter(
    (tile) => (telemetryOn || !tile.soon) && allowedTileKeys[tile.key]
  )

  return (
    <Card variant="outlined" className={styles.kpiStrip}>
      <Flex>
        {visibleTiles.map((tile, index) => (
          <Fragment key={tile.key}>
            <Link
              to={tile.href}
              className={styles.kpiTile}
              aria-label={tile.label}
            >
              <Flex align="center" gap={6}>
                <span className={styles.kpiTileIcon} aria-hidden>
                  {tile.icon}
                </span>
                <Text className={styles.kpiTileLabel}>{tile.label}</Text>
                {tile.soon ? <SoonTag /> : null}
              </Flex>
              {tile.loading ? (
                <Skeleton.Button
                  active
                  size="small"
                  style={{ width: 48, marginTop: 2 }}
                />
              ) : (
                <Text className={styles.kpiTileValue}>
                  {typeof tile.value === 'number'
                    ? tile.value.toLocaleString()
                    : (tile.value ?? '—')}
                </Text>
              )}
            </Link>
            {index < visibleTiles.length - 1 ? (
              <div className={styles.kpiDivider} aria-hidden />
            ) : null}
          </Fragment>
        ))}
      </Flex>
    </Card>
  )
}
